import { useEffect } from "react";
//-tech-debt-react-query;
import { keepPreviousData, useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useFetchPov } from "../components/Opportunity/opportunityDetailHelpers";
import { DEFAULT_QUERY_OPTIONS } from "../support/constants";
import { checkAndTagSE, createSEActivityForOpportunity } from "../support/opportunityHelpers";
import {
  deleteItem,
  fetchItems,
  postItem,
  putItem,
} from "../support/queries";
import { updateSENote } from "../support/salesForceHelpers";
import { PartialWithId } from "../types/base-types";
import { CacheKey } from "../types/cache-keys";
import { OpportunityProduct, RecordType } from "../types/opportunity";
import {
  PoVOutcome,
  PovPrereq,
  PovTeam,
  PovTeamMember,
  TestCase,
  UseCase,
} from "../types/opportunity-pov-items";
import { SalesforceTaskResponse } from "../types/salesforce";
import {
  useAddItemMutation,
  useDeleteItemMutation,
  useFetchItems,
  useUpdateItemMutation,
} from "./base-hooks";
import { useFetchOpportunity } from "./opportunity-hooks";

const USECASE_FETCH_CONCURRENCY = 6;

// Basic concurrency limiter (keeps us from firing N requests at once)
const mapLimit = async <T, R>(
  items: T[],
  limit: number,
  worker: (item: T) => Promise<R>
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (nextIndex < items.length) {
      const current = nextIndex++;
      results[current] = await worker(items[current]);
    }
  });

  await Promise.all(runners);
  return results;
};

const useFetchPovOutcomes = (opportunityId, options?) => {
  const { data: pov } = useFetchPov(
    { Id: opportunityId } as RecordType,
    { enabled: !!opportunityId }
  );

  return useFetchItems<PoVOutcome[]>(
    `db/pov-outcome`,
    () => [CacheKey.PovOutcomes, pov?.id],
    { pov: pov?.id },
    { ...DEFAULT_QUERY_OPTIONS, enabled: !!pov?.id, ...options }
  );
};

/**
 * FIXED:
 * - pov-usecase does not have a `pov` field, so `?pov=<id>` is ignored by the API.
 * - Instead, fetch outcomes by pov, then fetch usecases by `pov_outcome=<outcomeId>`.
 *
 * NOTE:
 * - This returns a flat UseCase[] exactly like before, so existing UI logic remains the same.
 * - No fetchAllPages used (per your note that filtered sets will never approach 1000).
 */
const useFetchPovUseCases = (opportunityId, options?) => {
  const { data: pov } = useFetchPov(
    { Id: opportunityId } as RecordType,
    { enabled: !!opportunityId }
  );

  const { data: outcomes = [], isLoading: outcomesLoading } =
    useFetchPovOutcomes(opportunityId);

  return useQuery<UseCase[]>({
    queryKey: [
      CacheKey.PovUseCases,
      String(pov?.id ?? ""),
      outcomes
        .map((o) => o?.id)
        .filter(Boolean)
        .sort((a, b) => a - b)
        .join(","),
    ],
    queryFn: async () => {
      const outcomeIds = outcomes.map((o) => o?.id).filter(Boolean);
      if (!outcomeIds.length) return [];

      // fetchItems returns `data.items` automatically for this API shape, i.e. UseCase[]
      const perOutcome = await mapLimit(
        outcomeIds,
        USECASE_FETCH_CONCURRENCY,
        (outcomeId) => fetchItems("db/pov-usecase", { pov_outcome: outcomeId }) as Promise<UseCase[]>
      );

      // flatten + de-dupe
      const flat = perOutcome.flat();
      const byId = new Map<number, UseCase>();
      flat.forEach((uc: any) => byId.set(uc.id, uc));
      return Array.from(byId.values());
    },
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        enabled: !!pov?.id && !outcomesLoading,
        ...options,
      },
  });
};

const useFetchOutcomeUseCases = (opportunityId: string, outcomeID: number, options?) => {
  const { data: useCases = [], isLoading: useCasesLoading } =
    useFetchPovUseCases(opportunityId, options);

  if (!outcomeID) return { data: [], isLoading: false };

  const useCasesForOutcome = useCases.filter(
    (useCase) => useCase.pov_outcome === outcomeID
  );

  return { data: useCasesForOutcome?.length ? useCasesForOutcome : [], isLoading: useCasesLoading };
};

const useFetchPovTeam = (pov: number, options?): UseQueryResult<PovTeam, unknown> =>
  useQuery({
    queryKey: [CacheKey.PovTeam, String(pov)],
    queryFn: () => fetchItems(`db/pov-team`, { pov: pov }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        placeholderData: keepPreviousData,
        enabled: !!pov,
        select: (data: PovTeam[] = []) => (Array.isArray(data) && data.length > 0 ? data[0] : null),
        ...options,
      },
  });

const useFetchPovPrereqs = (pov: number, options?): UseQueryResult<PovPrereq[], unknown> =>
  useQuery({
    queryKey: [CacheKey.PovPrereqs, String(pov)],
    queryFn: () => fetchItems(`db/pov-prereq`, { pov: pov }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        enabled: !!pov,
        select: (data: PovPrereq[] = []) => (Array.isArray(data) && data.length > 0 ? data : null),
        ...options,
      },
  });

const useFetchPovTeamFromSalesforce = (salesforceId, options?): UseQueryResult<PovTeamMember[], unknown> =>
  useQuery({
    queryKey: [CacheKey.SalesforceCustomerTeam, salesforceId],
    queryFn: () =>
      fetchItems(`sf/query`, {
        q: `SELECT Id, FirstName, LastName FROM Contact WHERE AccountId = '${salesforceId}'`,
      }),
    ...{
        ...DEFAULT_QUERY_OPTIONS,
        ...options,
        enabled: !!salesforceId && ((options && options["enabled"]) ?? true),
      },
  });

const useAddPovOutcomeMutation = () =>
  useAddItemMutation<Omit<PoVOutcome, "useCases">>(
    "db/pov-outcome",
    (payload) => [CacheKey.PovOutcomes, payload.pov]
  );

const useAddPovUseCaseMutation = (povId: string, options?: Record<string, any>) => {
  const queryClient = useQueryClient();

  const mutation = useAddItemMutation<Omit<UseCase, "testCases">>(
    "db/pov-usecase",
    (payload) => [CacheKey.PovUseCases, povId]
  );

  useEffect(() => {
    if (mutation.isSuccess) {
      queryClient.invalidateQueries({ queryKey: [CacheKey.PovOutcomes, povId].map(String) });
    }
  }, [mutation.isSuccess, povId, queryClient]);

  return mutation;
};

const useUpdatePovOutcomeMutation = () =>
  useUpdateItemMutation<Omit<PoVOutcome, "useCases">>(
    "db/pov-outcome",
    (payload) => [CacheKey.PovOutcomes, payload.pov, payload.id]
  );

const useUpdatePovUseCaseMutation = (povId: string) => {
  const queryClient = useQueryClient();

  const mutation = useUpdateItemMutation<Omit<UseCase, "testCases">>(
    "db/pov-usecase",
    (payload) => [CacheKey.PovUseCases, povId, payload.id]
  );

  useEffect(() => {
    if (mutation.isSuccess) {
      queryClient.invalidateQueries({ queryKey: [CacheKey.PovOutcomes, povId].map(String) });
    }
  }, [mutation.isSuccess, povId, queryClient]);

  return mutation;
};

const useDeletePovOutcomeMutation = () => useDeleteItemMutation<PartialWithId<PoVOutcome> & { pov: PoVOutcome['id'] }>(
  'db/pov-outcome',
  (payload) => [CacheKey.PovOutcomes, payload.pov],
);

const useDeletePovUseCaseMutation = (povId: string) => {
  const queryClient = useQueryClient();

  const mutation = useDeleteItemMutation<PartialWithId<UseCase>>(
    'db/pov-usecase',
    (payload) => [CacheKey.PovUseCases, povId, payload.id],
  );

  useEffect(() => {
    if (mutation.isSuccess) {
      queryClient.invalidateQueries({ queryKey: [CacheKey.PovOutcomes, povId].map(String) });
    }
  }, [mutation.isSuccess, povId, queryClient]);

  return mutation;
};

const useUpdatePovTeamMutation = () =>
  useUpdateItemMutation<PovTeam>(
    "db/pov-team",
    (payload) => [CacheKey.PovTeam, payload.pov]
  );

const useCreatePovTeamMutation = () =>
  useAddItemMutation<PovTeam>(
    "db/pov-team",
    (payload) => [CacheKey.PovTeam, payload.pov]
  );

const useCreatePovPrereqMutation = () =>
  useAddItemMutation<PovPrereq>(
    "db/pov-prereq",
    (payload) => [CacheKey.PovPrereqs, payload.pov]
  );

const useUpdatePovPrereqMutation = () =>
  useUpdateItemMutation<PovPrereq>(
    "db/pov-prereq",
    (payload) => [CacheKey.PovPrereqs, payload.pov]
  );

const useDeletePovPrereqMutation = () => useDeleteItemMutation<PartialWithId<PovPrereq>>(
  'db/pov-prereq',
  (payload) => [CacheKey.PovPrereqs, payload.id]
);

interface CreatePoVActivityResult {
  taskResponse: SalesforceTaskResponse | null;
  error: string | null;
}

const useCreatePoVActivity = ( opportunityId: string ) => {
  const queryClient = useQueryClient();

  // Fetch all required data at hook level (not inside mutation)
  const { data: opportunity } = useFetchOpportunity(opportunityId);
  const { data: pov } = useFetchPov(opportunity);
  const { data: povOutcomes } = useFetchPovOutcomes(opportunityId);
  const { data: povUseCases } = useFetchPovUseCases(opportunityId);
  const { data: povTeam } = useFetchPovTeam(pov?.id);
  const { data: sfPovTeam } = useFetchPovTeamFromSalesforce(opportunity?.AccountId);

  return useMutation<CreatePoVActivityResult, Error, number>({
    mutationFn: async (productId): Promise<CreatePoVActivityResult> => {
      if (!opportunity || !pov) {
        throw new Error('Missing required data for PoV activity creation');
      }

      const allProducts = queryClient.getQueryData<OpportunityProduct[]>([
        CacheKey.OpportunityProductAssignment,
        opportunityId,
      ]);

      const selectedProduct = allProducts?.find((p) => p.id === productId);

      if (!selectedProduct) {
        throw new Error(`Product ${productId} not found in cache`);
      }

      try {
        await checkAndTagSE(opportunity);
        const subject = `${opportunity.Name} - ${selectedProduct.product.name} - POV`;

        let details = '';

        const teamMembers = [];
        if (povTeam?.cust_bdm) {
          const bdm = sfPovTeam?.find((m) => m.Id === povTeam.cust_bdm);
          if (bdm) teamMembers.push(`BDM: ${bdm.FirstName} ${bdm.LastName}`);
        }
        if (povTeam?.cust_tdm) {
          const tdm = sfPovTeam?.find((m) => m.Id === povTeam.cust_tdm);
          if (tdm) teamMembers.push(`TDM: ${tdm.FirstName} ${tdm.LastName}`);
        }
        if (povTeam?.cust_champ) {
          const champ = sfPovTeam?.find((m) => m.Id === povTeam.cust_champ);
          if (champ) teamMembers.push(`Champion: ${champ.FirstName} ${champ.LastName}`);
        }
        if (teamMembers.length > 0) {
          details += 'Team:\n' + teamMembers.join('\n') + '\n\n';
        }

        const productUseCases =
          povUseCases?.filter((uc) => uc.product === selectedProduct.product_id) || [];
        const relevantOutcomeIds = new Set(productUseCases.map((uc) => uc.pov_outcome));
        const relevantOutcomes =
          povOutcomes?.filter((outcome) => relevantOutcomeIds.has(outcome.id)) || [];

        if (relevantOutcomes.length > 0) {
          details += 'Outcomes, Use Cases and Test Cases:\n';
          relevantOutcomes.forEach((outcome) => {
            details += `- ${outcome.title}\n`;
            const outcomeCases = productUseCases.filter((uc) => uc.pov_outcome === outcome.id);
            outcomeCases.forEach((useCase) => {
              details += `  - ${useCase.title}\n`;
              useCase.testCases?.forEach((testCase) => {
                details += `    - ${testCase.title}\n`;
              });
            });
          });
        }

        const taskResponse = await createSEActivityForOpportunity(opportunity, {
          summary: subject,
          details: details.trim(),
          serviceType: 'POV',
          technology: selectedProduct.product.sf_technology,
          dueDate: selectedProduct.planned_end_date,
        });

        await updateSENote(opportunity, `${subject} - PoV Activity Created`);

        return {
          taskResponse,
          error: null,
        };
      } catch (error) {
        console.error('Error creating PoV activity:', error);
        return {
          taskResponse: null,
          error: (error as Error).message,
        };
      }
    },
  });
};

/* Fetch/Mutations for validation form - data cached by pov and product.
   The form is responsible for managing data update behaviour so the generic optimistic functions not required here.
*/

const useFetchPovItemsForProduct = (opportunityId, productId, options: Record<string, any> = {}) => {
  const { data: pov } = useFetchPov({ Id: opportunityId } as RecordType, options);

  return useFetchItems<any[]>(
    `db/pov/${pov?.id}/product/${productId}`,
    () => [CacheKey.PovOutcomes, pov?.id, productId],
    {},
    {
      ...DEFAULT_QUERY_OPTIONS,
      ...options,
      enabled: Object.keys(options).includes("enabled") ? options.enabled && !!pov?.id : !!pov?.id,
    }
  );
};

const usePovMutation = <TInput,>(
  opportunityId: string,
  requestFn: (variables: TInput) => Promise<any>
) => {
  const queryClient = useQueryClient();

  const { data: pov } = useFetchPov(
    { Id: opportunityId } as RecordType,
    { enabled: !!opportunityId }
  );

  return useMutation<Record<string, any>, Error, TInput, Record<string, any>>({
    mutationFn: requestFn,
    onSettled: (_response, _variables, context) => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.PovOutcomes, pov?.id].map(String) });
      queryClient.invalidateQueries({ queryKey: [CacheKey.PovUseCases, pov?.id].map(String) });
    },
  });
};

const useFormAddPovTestCaseMutation = (opportunityId: string) => {
  return usePovMutation<Omit<TestCase, "id">>(opportunityId, (payload) =>
    postItem("db/pov-test-case", payload)
  );
};

const useFormUpdatePovTestCaseMutation = (opportunityId: string) => {
  return usePovMutation<TestCase>(opportunityId, (payload) =>
    putItem("db/pov-test-case", payload)
  );
};

const useFormDeletePovTestCaseMutation = (opportunityId: string) => {
  return usePovMutation<TestCase>(opportunityId, (payload) =>
    deleteItem(`db/pov-test-case/${payload.id}`)
  );
};

const useFormAddPovUseCaseMutation = (opportunityId: string) => {
  return usePovMutation<Omit<UseCase, "id">>(opportunityId, (payload) =>
    postItem("db/pov-usecase", payload)
  );
};

const useFormUpdatePovUseCaseMutation = (opportunityId: string) => {
  return usePovMutation<UseCase>(opportunityId, (payload) =>
    putItem("db/pov-usecase", payload)
  );
};

const useFormDeletePovUseCaseMutation = (opportunityId: string) => {
  return usePovMutation<UseCase>(opportunityId, (payload) =>
    deleteItem(`db/pov-usecase/${payload.id}`)
  );
};

const useFormAddPovOutcomeMutation = (opportunityId: string) => {
  return usePovMutation<Omit<PoVOutcome, "id">>(opportunityId, (payload) =>
    postItem("db/pov-outcome", payload)
  );
};

const useFormUpdatePovOutcomeMutation = (opportunityId: string) => {
  return usePovMutation<PoVOutcome>(opportunityId, (payload) =>
    putItem("db/pov-outcome", payload)
  );
};

const useFormDeletePovOutcomeMutation = (opportunityId: string) => {
  return usePovMutation<PoVOutcome>(opportunityId, (payload) =>
    deleteItem(`db/pov-outcome/${payload.id}`)
  );
};

export {

  useAddPovOutcomeMutation, useAddPovUseCaseMutation, useCreatePoVActivity, useCreatePovPrereqMutation, useCreatePovTeamMutation, useDeletePovOutcomeMutation, useDeletePovPrereqMutation, useDeletePovUseCaseMutation, useFetchOutcomeUseCases, useFetchPovItemsForProduct, useFetchPovOutcomes, useFetchPovPrereqs, useFetchPovTeam,
  useFetchPovTeamFromSalesforce, useFetchPovUseCases, useFormAddPovOutcomeMutation, useFormAddPovTestCaseMutation, useFormAddPovUseCaseMutation, useFormDeletePovOutcomeMutation, useFormDeletePovTestCaseMutation, useFormDeletePovUseCaseMutation, useFormUpdatePovOutcomeMutation, useFormUpdatePovTestCaseMutation, useFormUpdatePovUseCaseMutation, useUpdatePovOutcomeMutation, useUpdatePovPrereqMutation, useUpdatePovTeamMutation, useUpdatePovUseCaseMutation
};
